import { useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate, useParams, Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { FileText, FilePlus, FolderOpen, MessageSquare, ClipboardCheck, Stethoscope, Star, ArrowRight, ArrowLeft, Check, Lock } from "lucide-react";
import SignatureCanvas from "@/components/SignatureCanvas";
import FileUploader from "@/components/FileUploader";
import MessagesPanel from "@/components/MessagesPanel";
import { useAuth } from "@/lib/AuthContext";

const NAV = [
  { to: "/patient", end: true, label: "Overview", icon: ClipboardCheck },
  { to: "/patient/new", label: "New Pre-Op", icon: FilePlus },
  { to: "/patient/registrations", label: "My Registrations", icon: FileText },
  { to: "/patient/documents", label: "Documents", icon: FolderOpen },
  { to: "/patient/postop", label: "Post-Op", icon: Stethoscope },
  { to: "/patient/messages", label: "Messages", icon: MessageSquare },
  { to: "/patient/reviews", label: "Rate Care", icon: Star },
];

const STATUS_BADGE = {
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  reviewed: "bg-green-50 text-green-700 border-green-200",
  draft: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function PatientDashboard() {
  return (
    <AppShell items={NAV}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="new" element={<RegistrationForm />} />
        <Route path="registrations" element={<RegistrationsList />} />
        <Route path="registrations/:rid" element={<RegistrationView />} />
        <Route path="documents" element={<DocumentsTab />} />
        <Route path="postop" element={<PostOpTab />} />
        <Route path="messages" element={<MessagesTab />} />
        <Route path="reviews" element={<ReviewsTab />} />
        <Route path="*" element={<Navigate to="/patient" replace />} />
      </Routes>
    </AppShell>
  );
}

function Overview() {
  const { user } = useAuth();
  const [regs, setRegs] = useState([]);
  useEffect(() => { api.get("/patient/registrations").then((r) => setRegs(r.data || [])).catch(() => {}); }, []);
  const submitted = regs.filter((r) => r.status !== "reviewed").length;
  return (
    <div className="space-y-6" data-testid="patient-overview">
      <div>
        <p className="aamg-label">Patient portal</p>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-slate-500 mt-1">Complete your pre-op forms, share documents, and stay in touch with your care team.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard title="Active registrations" value={submitted} icon={FileText} accent="#339999" />
        <StatCard title="Reviewed" value={regs.filter((r) => r.status === "reviewed").length} icon={Check} accent="#22C55E" />
        <StatCard title="Total submissions" value={regs.length} icon={ClipboardCheck} accent="#E0A96D" />
      </div>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">Get started</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <Link to="/patient/new" className="group border border-slate-200 rounded-lg p-5 hover:border-[#339999] transition-colors" data-testid="overview-new-preop">
            <FilePlus className="h-6 w-6 text-[#339999] mb-2" />
            <div className="font-semibold text-slate-900">Start a new pre-op registration</div>
            <div className="text-sm text-slate-500 mt-1">Complete the 8-section intake form before your procedure.</div>
            <ArrowRight className="h-4 w-4 mt-3 text-[#339999] group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/patient/messages" className="group border border-slate-200 rounded-lg p-5 hover:border-[#339999] transition-colors">
            <MessageSquare className="h-6 w-6 text-[#339999] mb-2" />
            <div className="font-semibold text-slate-900">Message your anesthetist</div>
            <div className="text-sm text-slate-500 mt-1">Secure HIPAA-aligned chat with your care team.</div>
            <ArrowRight className="h-4 w-4 mt-3 text-[#339999] group-hover:translate-x-1 transition-transform" />
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, accent }) {
  return (
    <Card className="aamg-card">
      <CardContent className="p-5 flex items-center gap-4">
        <div className="h-11 w-11 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{value}</div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{title}</div>
        </div>
      </CardContent>
    </Card>
  );
}

const SECTIONS = ["Personal", "Contact", "Employment", "Next of Kin", "Insurance", "Surgery", "Attachments", "Certification"];

const EMPTY = {
  surname: "", first_name: "", second_name: "", dob: "", marital_status: "", nationality: "", national_insurance: "",
  street_address: "", po_box: "", email: "", phone_home: "", phone_work: "", phone_cell: "",
  employer: "", occupation: "", employer_address: "", employer_po_box: "",
  kin_name: "", kin_relationship: "", kin_phone_1: "", kin_phone_2: "", kin_phone_3: "", kin_address: "", kin_po_box: "",
  ins_provider: "", ins_policy_holder: "", ins_policy_no: "", ins_group: "", ins_certificate: "",
  ins2_provider: "", ins2_policy_holder: "", ins2_policy_no: "", ins2_group: "", ins2_certificate: "",
  surgery_type: "", surgeon: "", surgery_location: "", location_type: "", surgery_date: "", surgery_time: "", surgery_notes: "",
  cert_name: "", cert_relationship: "", cert_date: "", signature_data: "", attachments: [],
};

function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <Label className="field-label">{label}</Label>
      {children}
    </div>
  );
}

function RegistrationForm() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...EMPTY, email: user?.email || "" });
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    if (!sigRef.current?.isEmpty() === false) {
      // signature is empty
      toast.error("Please sign the form before submitting.");
      return;
    }
    setBusy(true);
    const payload = { ...form, signature_data: sigRef.current?.getDataUrl() || "" };
    try {
      const { data } = await api.post("/patient/registrations", payload);
      toast.success("Pre-op registration submitted");
      nav(`/patient/registrations/${data.registration_id}`);
    } catch (e) {
      toast.error("Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="patient-new-preop">
      <div>
        <p className="aamg-label">Pre-Op intake</p>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>New registration</h1>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {SECTIONS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)} data-testid={`section-tab-${i}`}
            className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap border ${i === step ? "bg-[#339999] text-white border-[#339999]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {i + 1}. {s}
          </button>
        ))}
      </div>

      <Card className="aamg-card">
        <CardContent className="p-6">
          {step === 0 && (
            <div className="form-grid">
              <Field label="Surname"><Input value={form.surname} onChange={set("surname")} data-testid="reg-surname" /></Field>
              <Field label="First name"><Input value={form.first_name} onChange={set("first_name")} data-testid="reg-first-name" /></Field>
              <Field label="Second name"><Input value={form.second_name} onChange={set("second_name")} /></Field>
              <Field label="Date of birth"><Input type="date" value={form.dob} onChange={set("dob")} data-testid="reg-dob" /></Field>
              <Field label="Marital status">
                <Select value={form.marital_status} onValueChange={(v) => setVal("marital_status", v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {["Single","Married","Divorced","Widowed","Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Nationality"><Input value={form.nationality} onChange={set("nationality")} /></Field>
              <Field label="National Insurance #" span={2}><Input value={form.national_insurance} onChange={set("national_insurance")} /></Field>
            </div>
          )}
          {step === 1 && (
            <div className="form-grid">
              <Field label="Street address" span={2}><Input value={form.street_address} onChange={set("street_address")} /></Field>
              <Field label="P.O. Box"><Input value={form.po_box} onChange={set("po_box")} /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} /></Field>
              <Field label="Phone (home)"><Input value={form.phone_home} onChange={set("phone_home")} /></Field>
              <Field label="Phone (work)"><Input value={form.phone_work} onChange={set("phone_work")} /></Field>
              <Field label="Phone (cell)" span={2}><Input value={form.phone_cell} onChange={set("phone_cell")} data-testid="reg-phone-cell" /></Field>
            </div>
          )}
          {step === 2 && (
            <div className="form-grid">
              <Field label="Employer"><Input value={form.employer} onChange={set("employer")} /></Field>
              <Field label="Occupation"><Input value={form.occupation} onChange={set("occupation")} /></Field>
              <Field label="Employer address" span={2}><Input value={form.employer_address} onChange={set("employer_address")} /></Field>
              <Field label="Employer P.O. Box"><Input value={form.employer_po_box} onChange={set("employer_po_box")} /></Field>
            </div>
          )}
          {step === 3 && (
            <div className="form-grid">
              <Field label="Name"><Input value={form.kin_name} onChange={set("kin_name")} /></Field>
              <Field label="Relationship"><Input value={form.kin_relationship} onChange={set("kin_relationship")} /></Field>
              <Field label="Phone 1"><Input value={form.kin_phone_1} onChange={set("kin_phone_1")} /></Field>
              <Field label="Phone 2"><Input value={form.kin_phone_2} onChange={set("kin_phone_2")} /></Field>
              <Field label="Phone 3"><Input value={form.kin_phone_3} onChange={set("kin_phone_3")} /></Field>
              <Field label="Address" span={2}><Input value={form.kin_address} onChange={set("kin_address")} /></Field>
              <Field label="P.O. Box"><Input value={form.kin_po_box} onChange={set("kin_po_box")} /></Field>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h3 className="aamg-section-title">Primary insurance</h3>
                <div className="form-grid">
                  <Field label="Provider"><Input value={form.ins_provider} onChange={set("ins_provider")} /></Field>
                  <Field label="Policy holder"><Input value={form.ins_policy_holder} onChange={set("ins_policy_holder")} /></Field>
                  <Field label="Policy #"><Input value={form.ins_policy_no} onChange={set("ins_policy_no")} /></Field>
                  <Field label="Group"><Input value={form.ins_group} onChange={set("ins_group")} /></Field>
                  <Field label="Certificate #" span={2}><Input value={form.ins_certificate} onChange={set("ins_certificate")} /></Field>
                </div>
              </div>
              <div>
                <h3 className="aamg-section-title">Secondary insurance (optional)</h3>
                <div className="form-grid">
                  <Field label="Provider"><Input value={form.ins2_provider} onChange={set("ins2_provider")} /></Field>
                  <Field label="Policy holder"><Input value={form.ins2_policy_holder} onChange={set("ins2_policy_holder")} /></Field>
                  <Field label="Policy #"><Input value={form.ins2_policy_no} onChange={set("ins2_policy_no")} /></Field>
                  <Field label="Group"><Input value={form.ins2_group} onChange={set("ins2_group")} /></Field>
                  <Field label="Certificate #" span={2}><Input value={form.ins2_certificate} onChange={set("ins2_certificate")} /></Field>
                </div>
              </div>
            </div>
          )}
          {step === 5 && (
            <div className="form-grid">
              <Field label="Surgery type"><Input value={form.surgery_type} onChange={set("surgery_type")} data-testid="reg-surgery-type" /></Field>
              <Field label="Surgeon"><Input value={form.surgeon} onChange={set("surgeon")} /></Field>
              <Field label="Surgery location"><Input value={form.surgery_location} onChange={set("surgery_location")} /></Field>
              <Field label="Location type">
                <Select value={form.location_type} onValueChange={(v) => setVal("location_type", v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {["Hospital","Medical Centre","Dental Centre"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Surgery date"><Input type="date" value={form.surgery_date} onChange={set("surgery_date")} /></Field>
              <Field label="Surgery time"><Input type="time" value={form.surgery_time} onChange={set("surgery_time")} /></Field>
              <Field label="Notes" span={2}><Textarea rows={3} value={form.surgery_notes} onChange={set("surgery_notes")} /></Field>
            </div>
          )}
          {step === 6 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Upload medical history, lab results, scans, or other relevant files (max 25 MB each).</p>
              <FileUploader value={form.attachments} onChange={(att) => setVal("attachments", att)} accept="application/pdf,image/*" />
            </div>
          )}
          {step === 7 && (
            <div className="space-y-5">
              <p className="text-sm text-slate-500">By signing below, you certify the information provided is accurate to the best of your knowledge.</p>
              <div className="form-grid">
                <Field label="Signed by (full name)"><Input value={form.cert_name} onChange={set("cert_name")} data-testid="reg-cert-name" /></Field>
                <Field label="Relationship to patient"><Input value={form.cert_relationship} onChange={set("cert_relationship")} placeholder="Self / Parent / Guardian" /></Field>
                <Field label="Date" span={2}><Input type="date" value={form.cert_date} onChange={set("cert_date")} /></Field>
              </div>
              <div>
                <Label className="field-label">Signature</Label>
                <SignatureCanvas ref={sigRef} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="reg-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        {step < SECTIONS.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="reg-next-btn">
            Next <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={busy} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="reg-submit-btn">
            {busy ? "Submitting…" : "Submit registration"}
          </Button>
        )}
      </div>
    </div>
  );
}

function RegistrationsList() {
  const [regs, setRegs] = useState([]);
  useEffect(() => { api.get("/patient/registrations").then((r) => setRegs(r.data || [])); }, []);
  return (
    <div className="space-y-5" data-testid="patient-registrations-list">
      <div className="flex items-center justify-between">
        <div>
          <p className="aamg-label">Submissions</p>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>My registrations</h1>
        </div>
        <Link to="/patient/new"><Button className="bg-[#339999] hover:bg-[#2A7F7F]"><FilePlus className="h-4 w-4 mr-2" />New</Button></Link>
      </div>
      <Card className="aamg-card">
        <CardContent className="p-0 overflow-x-auto">
          {regs.length === 0 ? (
            <div className="p-12 text-center text-slate-400">No registrations yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {regs.map((r) => (
                <li key={r.registration_id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div>
                    <div className="font-medium text-slate-900">{r.surgery_type || "Pre-op registration"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Submitted {r.submitted_at?.slice(0, 10) || "—"} · ID {r.registration_id?.slice(0, 8)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`border ${STATUS_BADGE[r.status] || STATUS_BADGE.draft} capitalize`}>{r.status}</Badge>
                    <Link to={`/patient/registrations/${r.registration_id}`}>
                      <Button variant="outline" size="sm" data-testid={`view-reg-${r.registration_id}`}>View</Button>
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RegistrationView() {
  const { rid } = useParams();
  const [reg, setReg] = useState(null);
  useEffect(() => { api.get(`/patient/registrations/${rid}`).then((r) => setReg(r.data)); }, [rid]);
  if (!reg) return <div className="text-slate-400">Loading…</div>;
  const locked = reg.status === "reviewed";
  return (
    <div className="space-y-5" data-testid="patient-reg-view">
      <div className="flex items-center justify-between">
        <div>
          <p className="aamg-label">Registration</p>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>{reg.surgery_type || "Pre-op registration"}</h1>
          <p className="text-slate-500 text-sm mt-1">ID {rid?.slice(0, 8)} · Submitted {reg.submitted_at?.slice(0, 10)}</p>
        </div>
        <Badge className={`border ${STATUS_BADGE[reg.status]} capitalize`}>{locked && <Lock className="h-3 w-3 mr-1" />}{reg.status}</Badge>
      </div>
      {locked && (
        <div className="rounded-md bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-2">
          This registration has been reviewed and locked. Contact AAMG staff for any updates.
        </div>
      )}
      <Card className="aamg-card">
        <CardContent className="p-6 space-y-6">
          <Section title="Personal">
            <KV label="Surname" value={reg.surname} /><KV label="First name" value={reg.first_name} />
            <KV label="Second name" value={reg.second_name} /><KV label="DOB" value={reg.dob} />
            <KV label="Marital status" value={reg.marital_status} /><KV label="Nationality" value={reg.nationality} />
            <KV label="NIB #" value={reg.national_insurance} />
          </Section>
          <Section title="Contact">
            <KV label="Address" value={reg.street_address} /><KV label="P.O. Box" value={reg.po_box} />
            <KV label="Email" value={reg.email} /><KV label="Phone (cell)" value={reg.phone_cell} />
            <KV label="Phone (home)" value={reg.phone_home} /><KV label="Phone (work)" value={reg.phone_work} />
          </Section>
          <Section title="Surgery">
            <KV label="Type" value={reg.surgery_type} /><KV label="Surgeon" value={reg.surgeon} />
            <KV label="Location" value={reg.surgery_location} /><KV label="Location type" value={reg.location_type} />
            <KV label="Date" value={reg.surgery_date} /><KV label="Time" value={reg.surgery_time} />
          </Section>
          {reg.signature_data && (
            <div>
              <h3 className="aamg-section-title">Signature</h3>
              <img src={reg.signature_data} alt="signature" className="border border-slate-200 rounded-md max-w-xs bg-white" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="aamg-section-title">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 pb-1.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm text-slate-900 text-right">{value || "—"}</span>
    </div>
  );
}

function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]);
  const refresh = () => api.get("/patient/documents").then((r) => setDocs(r.data || []));
  useEffect(() => { refresh(); }, []);
  const submit = async () => {
    if (!notes.trim()) { toast.error("Notes are required"); return; }
    try {
      await api.post("/patient/documents", { notes, attachments });
      setNotes(""); setAttachments([]); toast.success("Document submitted"); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
  };
  return (
    <div className="space-y-5" data-testid="patient-documents">
      <div>
        <p className="aamg-label">Records</p>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Documents</h1>
      </div>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">Submit a document</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label="Notes (required)"><Textarea rows={3} required value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Describe what you're submitting…" data-testid="doc-notes-input" /></Field>
          <FileUploader value={attachments} onChange={setAttachments} />
          <Button onClick={submit} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="doc-submit-btn">Submit</Button>
        </CardContent>
      </Card>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">My submissions</CardTitle></CardHeader>
        <CardContent>
          {docs.length === 0 ? <div className="text-slate-400 text-sm">No documents yet</div> : (
            <ul className="divide-y divide-slate-100">
              {docs.map((d) => (
                <li key={d.document_id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-900">{d.notes || "—"}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{(d.attachments?.length || 0)} file(s) · {d.created_at?.slice(0, 10)}</div>
                    </div>
                    <Badge className={`border ${STATUS_BADGE[d.status] || STATUS_BADGE.submitted} capitalize`}>{d.status}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PostOpTab() {
  const [list, setList] = useState([]);
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]);
  const refresh = () => api.get("/patient/postop-reviews").then((r) => setList(r.data || []));
  useEffect(() => { refresh(); }, []);
  const submit = async () => {
    if (!notes.trim()) { toast.error("Notes required"); return; }
    await api.post("/patient/postop-reviews", { notes, attachments });
    setNotes(""); setAttachments([]); toast.success("Post-op submitted"); refresh();
  };
  return (
    <div className="space-y-5" data-testid="patient-postop">
      <div><p className="aamg-label">Recovery</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Post-Op reviews</h1></div>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">Submit a post-op review</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label="Recovery notes"><Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="postop-notes-input" /></Field>
          <FileUploader value={attachments} onChange={setAttachments} />
          <Button onClick={submit} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="postop-submit-btn">Submit</Button>
        </CardContent>
      </Card>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">My post-op submissions</CardTitle></CardHeader>
        <CardContent>
          {list.length === 0 ? <div className="text-sm text-slate-400">No submissions yet</div> : (
            <ul className="divide-y divide-slate-100">
              {list.map((r) => (
                <li key={r.review_id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-slate-900 line-clamp-2">{r.notes}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{(r.attachments?.length || 0)} file(s) · {r.created_at?.slice(0, 10)}</div>
                  </div>
                  <Badge className={`border ${STATUS_BADGE[r.status] || STATUS_BADGE.submitted} capitalize`}>{r.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MessagesTab() {
  return (
    <div className="flex flex-col h-[calc(100dvh-180px)] md:h-[calc(100vh-160px)]" data-testid="patient-messages">
      <div className="shrink-0 mb-4">
        <p className="aamg-label">Care team</p>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Messages</h1>
      </div>
      <div className="flex-1 min-h-0">
        <MessagesPanel contactsEndpoint="/patient/contacts" />
      </div>
    </div>
  );
}

function ReviewsTab() {
  const [anesthetists, setAnesthetists] = useState([]);
  const [selected, setSelected] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  useEffect(() => { api.get("/patient/anesthetists").then((r) => setAnesthetists(r.data || [])); }, []);
  const submit = async () => {
    if (!selected || !rating) { toast.error("Select anesthetist and rating"); return; }
    await api.post("/patient/reviews", { anesthetist_id: selected, rating, comment });
    toast.success("Thank you for your feedback");
    setRating(0); setComment(""); setSelected("");
  };
  return (
    <div className="space-y-5 max-w-xl" data-testid="patient-reviews">
      <div><p className="aamg-label">Quality</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Rate your care</h1>
        <p className="text-slate-500 text-sm mt-1">Your rating is private and shared only with AAMG administrators.</p></div>
      <Card className="aamg-card">
        <CardContent className="p-6 space-y-4">
          <Field label="Anesthetist">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="review-anesthetist-select"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{anesthetists.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div>
            <Label className="field-label">Rating</Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                  <Star className={`h-7 w-7 ${n <= rating ? "fill-[#E0A96D] text-[#E0A96D]" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <Field label="Comment (optional)"><Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
          <Button onClick={submit} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="review-submit-btn">Submit rating</Button>
        </CardContent>
      </Card>
    </div>
  );
}
